import React from 'react'
import { Table, Button, Tag, Space, Avatar } from 'antd'
import { EyeOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import type { Brand } from '../types/brand.types'
import { DocumentAvatar } from '../../../shared/components/DocumentAvatar'

interface BrandsTableProps {
    brands: Brand[]
    onRowClick: (brand: Brand) => void
    onEdit: (brand: Brand) => void
    onDelete: (brand: Brand) => void
}

export const BrandsTable: React.FC<BrandsTableProps> = ({
    brands,
    onRowClick,
    onEdit,
    onDelete
}) => {
    const columns: ColumnsType<Brand> = [
        {
            title: 'Logo',
            dataIndex: 'logo',
            key: 'logo',
            render: (logo: string, record: Brand) => (
                logo ? (
                    <DocumentAvatar documentId={logo} size={40} />
                ) : record.logoUrl ? (
                    <Avatar src={record.logoUrl} size={40} />
                ) : (
                    <Avatar size={40} style={{ backgroundColor: '#7c3aed' }}>
                        {record.name.charAt(0)}
                    </Avatar>
                )
            ),
        },
        {
            title: 'Brand Name',
            dataIndex: 'name',
            key: 'name',
            render: (name: string) => <strong>{name}</strong>,
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
        },
        {
            title: 'Products',
            dataIndex: 'productCount',
            key: 'productCount',
            render: (count: number) => <Tag color="blue">{count} products</Tag>,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                <Tag color={status === 'active' ? 'green' : 'gray'}>{status.toUpperCase()}</Tag>
            ),
        },
        {
            title: 'Created',
            dataIndex: 'createdAt',
            key: 'createdAt',
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => onRowClick(record)}>
                        View
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={(e) => {
                            e.stopPropagation()
                            onEdit(record)
                        }}
                    >
                        Edit
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={(e) => {
                            e.stopPropagation()
                            onDelete(record)
                        }}
                    >
                        Delete
                    </Button>
                </Space>
            ),
        },
    ]

    return (
        <Table
            columns={columns}
            dataSource={brands}
            rowKey="id"
            onRow={(record) => ({
                onClick: () => onRowClick(record),
                style: { cursor: 'pointer' },
            })}
            pagination={{
                total: brands.length,
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} brands`,
            }}
        />
    )
}
